import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { TIMEPICKER_HOUR, TIMEPICKER_MINUTE, TIMEPICKER_SECOND, TIMEPICKER_MILLISECOND } from './classes.js';
import { get12HourFrom24Hour, get24HourFrom12Hour } from './dateUtils.js';

var _TimeUnitMetadata;
/** describes a component of time. `H:MM:SS.MS` */

var TimeUnit;
/** Returns the given time unit component of the date. */

(function (TimeUnit) {
  TimeUnit["HOUR_24"] = "hour24";
  TimeUnit["HOUR_12"] = "hour12";
  TimeUnit["MINUTE"] = "minute";
  TimeUnit["SECOND"] = "second";
  TimeUnit["MS"] = "ms";
})(TimeUnit || (TimeUnit = {}));

function getTimeUnit(unit, date) {
  switch (unit) {
    case TimeUnit.HOUR_24:
      return date.getHours();

    case TimeUnit.HOUR_12:
      return get12HourFrom24Hour(date.getHours());

    case TimeUnit.MINUTE:
      return date.getMinutes();

    case TimeUnit.SECOND:
      return date.getSeconds();

    case TimeUnit.MS:
      return date.getMilliseconds();

    default:
      throw Error("Invalid TimeUnit");
  }
}
/** Sets the given time unit to the given time in date object. Modifies given `date` object and returns it. */

function setTimeUnit(unit, time, date, isPm) {
  switch (unit) {
    case TimeUnit.HOUR_24:
      date.setHours(time);
      break;

    case TimeUnit.HOUR_12:
      date.setHours(get24HourFrom12Hour(time, isPm));
      break;

    case TimeUnit.MINUTE:
      date.setMinutes(time);
      break;

    case TimeUnit.SECOND:
      date.setSeconds(time);
      break;

    case TimeUnit.MS:
      date.setMilliseconds(time);
      break;

    default:
      throw Error("Invalid TimeUnit");
  }

  return date;
}
/** Returns true if `time` is a valid value */

function isTimeUnitValid(unit, time) {
  return time != null && !isNaN(time) && getTimeUnitMin(unit) <= time && time <= getTimeUnitMax(unit);
}
/** If unit of time is greater than max, returns min. If less than min, returns max. Otherwise, returns time. */

function wrapTimeAtUnit(unit, time) {
  var max = getTimeUnitMax(unit);
  var min = getTimeUnitMin(unit);

  if (time > max) {
    return min;
  } else if (time < min) {
    return max;
  }

  return time;
}
function getTimeUnitClassName(unit) {
  return TimeUnitMetadata[unit].className;
}
function getTimeUnitMax(unit) {
  return TimeUnitMetadata[unit].max;
}
function getTimeUnitMin(unit) {
  return TimeUnitMetadata[unit].min;
}
function getDefaultMinTime() {
  return new Date(0, 0, 0, DEFAULT_MIN_HOUR, DEFAULT_MIN_MINUTE, DEFAULT_MIN_SECOND, DEFAULT_MIN_MILLISECOND);
}
function getDefaultMaxTime() {
  return new Date(0, 0, 0, DEFAULT_MAX_HOUR, DEFAULT_MAX_MINUTE, DEFAULT_MAX_SECOND, DEFAULT_MAX_MILLISECOND);
}
var DEFAULT_MIN_HOUR = 0;
var MERIDIEM_MIN_HOUR = 1;
var DEFAULT_MIN_MINUTE = 0;
var DEFAULT_MIN_SECOND = 0;
var DEFAULT_MIN_MILLISECOND = 0;
var DEFAULT_MAX_HOUR = 23;
var MERIDIEM_MAX_HOUR = 12;
var DEFAULT_MAX_MINUTE = 59;
var DEFAULT_MAX_SECOND = 59;
var DEFAULT_MAX_MILLISECOND = 999;
/**
 * A datastore (internal to this file) mapping TimeUnits to useful information about them.
 * Use the `get*` methods above to access these fields.
 */

var TimeUnitMetadata = (_TimeUnitMetadata = {}, _defineProperty(_TimeUnitMetadata, TimeUnit.HOUR_24, {
  className: TIMEPICKER_HOUR,
  max: DEFAULT_MAX_HOUR,
  min: DEFAULT_MIN_HOUR
}), _defineProperty(_TimeUnitMetadata, TimeUnit.HOUR_12, {
  className: TIMEPICKER_HOUR,
  max: MERIDIEM_MAX_HOUR,
  min: MERIDIEM_MIN_HOUR
}), _defineProperty(_TimeUnitMetadata, TimeUnit.MINUTE, {
  className: TIMEPICKER_MINUTE,
  max: DEFAULT_MAX_MINUTE,
  min: DEFAULT_MIN_MINUTE
}), _defineProperty(_TimeUnitMetadata, TimeUnit.SECOND, {
  className: TIMEPICKER_SECOND,
  max: DEFAULT_MAX_SECOND,
  min: DEFAULT_MIN_SECOND
}), _defineProperty(_TimeUnitMetadata, TimeUnit.MS, {
  className: TIMEPICKER_MILLISECOND,
  max: DEFAULT_MAX_MILLISECOND,
  min: DEFAULT_MIN_MILLISECOND
}), _TimeUnitMetadata);

export { TimeUnit, getDefaultMaxTime, getDefaultMinTime, getTimeUnit, getTimeUnitClassName, getTimeUnitMax, getTimeUnitMin, isTimeUnitValid, setTimeUnit, wrapTimeAtUnit };
//# sourceMappingURL=timeUnit.js.map
